import { EMPTY_ATTRIBUTE, REQUIRED_SECTION } from './CreateDialogRequired';

const USABLE = ':not([disabled]):not([tabindex="-1"])';
const NOT_TEXT = ['hidden', 'checkbox', 'radio', 'file'].map((type) => `:not([type="${type}"])`).join('');
const TEXT_BOX = [`input${NOT_TEXT}`, 'textarea'].map((control) => control + USABLE).join(', ');
const SELECT = `[data-slot="select-trigger"]${USABLE}`;
const TEXT_CONTROL = `${TEXT_BOX}, ${SELECT}`;
/** Everything else the focus can go to. A radio of the kit is one: an element with a tab stop, not an input. */
const OTHER_CONTROL = ['button', '[tabindex]', '[contenteditable="true"]']
    .map((control) => control + USABLE)
    .join(', ');

/**
 * A text box with nothing typed and no chip beside it, a select that shows its placeholder, a radio of a group
 * with nothing chosen. Any other control goes by what draws it: a field or a block marked as left to fill, which
 * holds no text box or select the focus would rather go to.
 */
function isEmpty(control: HTMLElement): boolean {
    if (control.matches(SELECT)) {
        return control.hasAttribute('data-placeholder');
    }
    if (control.matches(TEXT_BOX)) {
        const chips = control.closest('[data-slot="combobox-chips"]');
        return (control as HTMLInputElement).value === '' && !chips?.querySelector('[data-slot="combobox-chip"]');
    }
    if (control.matches('[role="radio"]')) {
        return !control.closest('[role="radiogroup"]')?.querySelector('[aria-checked="true"]');
    }
    return control.closest(`[${EMPTY_ATTRIBUTE}]`)?.querySelector(TEXT_CONTROL) === null;
}

/**
 * Focuses the first empty control of the dialog's Required section, in the order on screen. `false` while the
 * form has not drawn its sections yet, so the caller tries again; `true` once it has, empty control or not.
 *
 * ponytail: read off the markup, which works for a body that hands the dialog no form too. What is neither a
 * text box, a select nor a radio group is only known through the mark its field or its block carries, so a field
 * inside a block that does not say whether it is filled (an image) is passed over. If that matters, have that
 * block say so.
 */
export function focusFirstEmptyRequired(dialogBody: Element): boolean {
    const required = dialogBody.querySelector(`[data-create-dialog-section="${REQUIRED_SECTION}"]`);

    if (!required) {
        return false;
    }
    focusFirstEmpty([required]);
    return true;
}

/** Focuses the first empty control under `roots`, taken in their order, each in the order on screen. */
export function focusFirstEmpty(roots: Element[]): void {
    roots
        .flatMap((root) => Array.from(root.querySelectorAll<HTMLElement>(`${TEXT_CONTROL}, ${OTHER_CONTROL}`)))
        .find(isEmpty)
        ?.focus();
}
